import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import {
  Building2,
  Users,
  Handshake,
  Globe2,
  Globe,
  CheckCircle2,
  ShieldCheck,
  Lightbulb,
  Target,
  Sparkles,
  ArrowRight,
} from "lucide-react";

const partnerTypes = [
  {
    icon: Building2,
    title: "Healthcare Institutions",
    description:
      "Partnering with hospitals, clinics, and healthcare facilities to deploy our innovative solutions at scale.",
    accent: "text-primary",
    bg: "bg-primary/10",
    bar: "from-primary to-primary/40",
  },
  {
    icon: Users,
    title: "Research Organizations",
    description:
      "Collaborating with research institutes to advance AI in healthcare and drive evidence-based medical innovation.",
    accent: "text-secondary",
    bg: "bg-secondary/10",
    bar: "from-secondary to-secondary/40",
  },
  {
    icon: Handshake,
    title: "Technology Partners",
    description:
      "Working with technology companies to enhance our platform capabilities, integrations, and market reach.",
    accent: "text-accent",
    bg: "bg-accent/10",
    bar: "from-accent to-accent/40",
  },
  {
    icon: Globe2,
    title: "Global Distribution",
    description:
      "Building distribution partnerships worldwide to make quality healthcare technology accessible across borders.",
    accent: "text-primary",
    bg: "bg-primary/10",
    bar: "from-primary to-primary/40",
  },
];

const weOffer = [
  "Cutting-edge health tech solutions",
  "AI-powered innovation and expertise",
  "Made in India quality at affordable prices",
  "Collaborative development approach",
  "Long-term growth partnerships",
];

const weSeek = [
  "Shared vision for healthcare transformation",
  "Commitment to quality and innovation",
  "Focus on accessibility and affordability",
  "Strong industry expertise and networks",
  "Long-term collaboration mindset",
];

const partnerValues = [
  {
    icon: ShieldCheck,
    title: "Trust",
    description: "Building every relationship on a foundation of transparency and mutual accountability.",
    accent: "text-primary",
    bg: "bg-primary/10",
  },
  {
    icon: Lightbulb,
    title: "Innovation",
    description: "Pushing the boundaries of what's possible in healthcare technology — together.",
    accent: "text-secondary",
    bg: "bg-secondary/10",
  },
  {
    icon: Target,
    title: "Impact",
    description: "Creating meaningful, measurable change in how healthcare is delivered and experienced.",
    accent: "text-accent",
    bg: "bg-accent/10",
  },
  {
    icon: Globe,
    title: "Global Reach",
    description: "Serving clinicians, patients, and communities across borders and time zones.",
    accent: "text-primary",
    bg: "bg-primary/10",
  },
];

const Associates = () => {
  return (
    <Layout>
      {/* ─── Hero ─────────────────────────────────────────────────────────── */}
      <section className="relative py-24 overflow-hidden bg-gradient-to-br from-primary/8 via-background to-secondary/8">
        <div className="absolute inset-0 bg-grid opacity-40 pointer-events-none" />
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-primary/5 rounded-full blur-3xl translate-x-1/2 -translate-y-1/4 pointer-events-none" />
        <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-secondary/5 rounded-full blur-3xl -translate-x-1/3 translate-y-1/3 pointer-events-none" />

        <div className="container mx-auto px-4 text-center relative">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-primary/10 border border-primary/20 text-primary text-sm font-semibold mb-8">
            <Sparkles className="h-3.5 w-3.5" />
            Associates &amp; Partners
          </div>
          <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold mb-6 leading-[1.1]">
            <span className="text-gradient-primary">Build the future</span>
            <br />
            <span className="text-foreground">of healthcare with us</span>
          </h1>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto leading-relaxed">
            Strategic partnerships and collaborations with industry leaders, institutions, and
            innovators worldwide — together transforming healthcare through technology.
          </p>
        </div>
      </section>

      {/* ─── Medical Associates ───────────────────────────────────────────── */}
      <section className="pt-12 pb-24 bg-background">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <span className="text-xs font-bold tracking-[0.18em] uppercase text-primary mb-3 block">
              Medical Associates
            </span>
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              <span className="text-gradient-primary">Our Clinical Experts</span>
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Eminent clinicians and academic leaders who bring deep domain expertise to guide
              our medical innovation.
            </p>
          </div>

          <div className="flex justify-center">
            <div className="rounded-3xl border border-border/60 bg-white shadow-[0_4px_24px_rgba(0,0,0,0.06)] hover:shadow-[0_12px_40px_rgba(0,0,0,0.12)] hover:-translate-y-1 transition-all duration-500 overflow-hidden max-w-xl w-full">
              <div className="h-[3px] w-full bg-gradient-to-r from-primary to-secondary" />

              {/* Mobile: stacked / Desktop: side-by-side */}
              <div className="flex flex-col md:flex-row">

                {/* Image */}
                <div className="flex justify-center pt-8 px-8 pb-4 md:py-8 md:pl-8 md:pr-5 shrink-0">
                  <img
                    src="/dr-parasar-ghosh.png"
                    alt="Dr. Parasar Ghosh"
                    className="w-36 h-44 md:w-52 md:h-64 object-cover object-top rounded-2xl shadow-md border border-border/20"
                  />
                </div>

                {/* Text */}
                <div className="flex-2 flex flex-col justify-center text-center md:text-left px-8 pb-8 md:py-8 md:pl-2 md:pr-8">
                  <h3 className="text-xl font-bold text-foreground mb-1">Dr. Parasar Ghosh</h3>
                  <p className="text-sm font-semibold text-primary mb-4">MD, DM</p>
                  <p className="text-muted-foreground text-xs leading-relaxed mb-5">
                    Professor and Head of the Department of Clinical Immunology and Rheumatology
                    at IPGMER &amp; SSKM Hospital, Kolkata. With 44 years of distinguished clinical
                    and academic experience, Dr. Ghosh is one of India's foremost authorities in
                    rheumatology and immunological disorders, bringing unparalleled expertise to
                    our medical advisory network.
                  </p>
                  <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-semibold self-center md:self-start">
                    44 Years of Experience
                  </div>
                </div>

              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── Partnership Opportunities ────────────────────────────────────── */}
      <section className="py-24 bg-background">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <span className="text-xs font-bold tracking-[0.18em] uppercase text-primary mb-3 block">
              Partnership Opportunities
            </span>
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              <span className="text-gradient-primary">Who we partner with</span>
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              We're actively building relationships with organizations that share our vision of
              transforming healthcare through technology.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-5xl mx-auto">
            {partnerTypes.map(({ icon: Icon, title, description, accent, bg, bar }) => (
              <div
                key={title}
                className="group rounded-3xl border border-border/60 bg-white shadow-[0_4px_24px_rgba(0,0,0,0.06)] hover:shadow-[0_12px_40px_rgba(0,0,0,0.12)] hover:-translate-y-1 transition-all duration-500 overflow-hidden"
              >
                <div className={`h-[3px] w-full bg-gradient-to-r ${bar}`} />
                <div className="p-8">
                  <div className={`h-12 w-12 rounded-2xl ${bg} flex items-center justify-center mb-5`}>
                    <Icon className={`h-6 w-6 ${accent}`} />
                  </div>
                  <h3 className="text-xl font-bold text-foreground mb-3">{title}</h3>
                  <p className="text-muted-foreground leading-relaxed">{description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── Offer / Seek ─────────────────────────────────────────────────── */}
      <section className="py-24 bg-gradient-to-b from-muted/40 to-background">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <span className="text-xs font-bold tracking-[0.18em] uppercase text-primary mb-3 block">
              Building Our Network
            </span>
            <h2 className="text-4xl font-bold mb-4">A partnership built on mutual value</h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              As a growing health-tech company, we're establishing partnerships that help us
              deliver on our mission of affordable, high-quality healthcare solutions.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            <div className="rounded-3xl border border-border/60 bg-white shadow-[0_4px_24px_rgba(0,0,0,0.06)] overflow-hidden">
              <div className="h-[3px] w-full bg-gradient-to-r from-primary to-primary/40" />
              <div className="p-8">
                <h3 className="text-xl font-bold text-foreground mb-6">What We Offer Partners</h3>
                <ul className="space-y-4">
                  {weOffer.map((item) => (
                    <li key={item} className="flex items-start gap-3">
                      <CheckCircle2 className="h-4 w-4 text-primary mt-0.5 shrink-0" />
                      <span className="text-muted-foreground text-sm leading-relaxed">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="rounded-3xl border border-border/60 bg-white shadow-[0_4px_24px_rgba(0,0,0,0.06)] overflow-hidden">
              <div className="h-[3px] w-full bg-gradient-to-r from-secondary to-secondary/40" />
              <div className="p-8">
                <h3 className="text-xl font-bold text-foreground mb-6">What We Seek in Partners</h3>
                <ul className="space-y-4">
                  {weSeek.map((item) => (
                    <li key={item} className="flex items-start gap-3">
                      <CheckCircle2 className="h-4 w-4 text-secondary mt-0.5 shrink-0" />
                      <span className="text-muted-foreground text-sm leading-relaxed">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── Partnership Values ───────────────────────────────────────────── */}
      <section className="py-24 bg-background">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4">Our Partnership Values</h2>
            <p className="text-lg text-muted-foreground max-w-xl mx-auto">
              The principles that guide every collaboration we enter.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 max-w-5xl mx-auto">
            {partnerValues.map(({ icon: Icon, title, description, accent, bg }) => (
              <div
                key={title}
                className="group rounded-2xl border border-border/60 bg-white shadow-[0_4px_20px_rgba(0,0,0,0.05)] hover:shadow-[0_10px_36px_rgba(0,0,0,0.10)] hover:-translate-y-1 transition-all duration-500 p-6 text-center"
              >
                <div className={`h-12 w-12 rounded-2xl ${bg} flex items-center justify-center mb-4 mx-auto`}>
                  <Icon className={`h-6 w-6 ${accent}`} />
                </div>
                <h3 className="font-bold text-foreground mb-2">{title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── CTA ──────────────────────────────────────────────────────────── */}
      <section className="py-20 bg-gradient-to-br from-primary via-primary/90 to-secondary relative overflow-hidden">
        <div className="absolute inset-0 bg-grid opacity-20 pointer-events-none" />
        <div className="container mx-auto px-4 text-center relative">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-5">Become a Partner</h2>
          <p className="text-xl text-white/85 mb-10 max-w-2xl mx-auto">
            Interested in partnering with Wellnext Technologies? We're always looking to connect
            with organizations that share our passion for transforming healthcare.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" variant="secondary" className="gap-2 rounded-xl text-base">
              Partner Inquiry
              <ArrowRight className="h-4 w-4" />
            </Button>
            <Button
              size="lg"
              variant="outline"
              className="gap-2 rounded-xl text-base border-white text-blue hover:bg-white/15 hover:text-white"
            >
              Download Partnership Brochure
            </Button>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default Associates;
